
import { useState } from 'react';
import { useCart } from '../../hooks/useCart';
import CartNotification from './CartNotification';

interface Product {
  id: string;
  image: string;
  brand: string;
  name: string;
  price: string;
  originalPrice?: string;
  isNew?: boolean;
}

interface ProductModalProps {
  product: Product;
  isOpen: boolean;
  onClose: () => void;
}

const ProductModal = ({ product, isOpen, onClose }: ProductModalProps) => {
  const { addItem } = useCart();
  const [selectedSize, setSelectedSize] = useState('M');
  const [quantity, setQuantity] = useState(1);
  const [showNotification, setShowNotification] = useState(false);

  if (!isOpen) return null;

  const sizes = ['P', 'M', 'G', 'GG'];

  const handleAddToCart = () => {
    addItem(
      {
        id: product.id,
        image: product.image,
        brand: product.brand,
        name: product.name,
        price: product.price
      },
      quantity,
      selectedSize
    );
    
    setShowNotification(true);
    setTimeout(() => {
      onClose();
    }, 1000);
  };

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity >= 1 && newQuantity <= 99) {
      setQuantity(newQuantity);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div 
          className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          style={{ backgroundColor: '#F8F8F8' }}
        >
          {/* Header */}
          <div className="flex justify-between items-center p-6 border-b" style={{ borderColor: '#C0C0C0' }}>
            <h2 
              className="text-2xl font-bold"
              style={{ 
                fontFamily: 'Cinzel Decorative, serif',
                color: '#0B0B0B'
              }}
            >
              {product.brand}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>

          {/* Product Details */}
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Product Image */}
              <div>
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-96 lg:h-[500px] object-cover rounded-lg"
                />
              </div>

              {/* Product Info */}
              <div>
                <h3 
                  className="text-2xl font-bold mb-4"
                  style={{ 
                    fontFamily: 'Playfair Display, serif',
                    color: '#0B0B0B'
                  }}
                >
                  {product.name}
                </h3>
                
                <div className="flex items-center space-x-3 mb-6">
                  <span 
                    className="text-3xl font-bold"
                    style={{ 
                      color: '#0F3F2F',
                      fontFamily: 'Montserrat, sans-serif'
                    }}
                  >
                    {product.price}
                  </span>
                  {product.originalPrice && (
                    <span 
                      className="text-xl line-through"
                      style={{ color: '#C0C0C0' }}
                    >
                      {product.originalPrice}
                    </span>
                  )}
                </div>

                {product.isNew && (
                  <span 
                    className="inline-block px-3 py-1 text-sm font-semibold text-white rounded-full mb-6"
                    style={{ backgroundColor: '#0F3F2F' }}
                  >
                    NOVO
                  </span>
                )}

                <p 
                  className="text-base mb-8"
                  style={{ 
                    color: '#C0C0C0',
                    fontFamily: 'Montserrat, sans-serif'
                  }}
                >
                  Peça premium da coleção DLUXMEN. Qualidade superior e design exclusivo para homens que valorizam estilo e sofisticação.
                </p>

                {/* Size Selection */}
                <div className="mb-6">
                  <h4 
                    className="text-sm font-bold mb-3"
                    style={{ 
                      color: '#0B0B0B',
                      fontFamily: 'Montserrat, sans-serif'
                    }}
                  >
                    Tamanho
                  </h4>
                  <div className="flex space-x-2">
                    {sizes.map((size) => (
                      <button
                        key={size}
                        onClick={() => setSelectedSize(size)}
                        className={`w-12 h-12 border rounded-lg font-medium transition-all duration-200 ${
                          selectedSize === size
                            ? 'border-green-800 bg-green-800 text-white'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                        style={{ 
                          fontFamily: 'Montserrat, sans-serif',
                          borderColor: selectedSize === size ? '#0F3F2F' : '#C0C0C0',
                          backgroundColor: selectedSize === size ? '#0F3F2F' : 'transparent',
                          color: selectedSize === size ? 'white' : '#0B0B0B'
                        }}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Quantity Selection */}
                <div className="mb-8">
                  <h4 
                    className="text-sm font-bold mb-3"
                    style={{ 
                      color: '#0B0B0B',
                      fontFamily: 'Montserrat, sans-serif'
                    }}
                  >
                    Quantidade
                  </h4>
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center border rounded-lg" style={{ borderColor: '#C0C0C0' }}>
                      <button
                        onClick={() => handleQuantityChange(quantity - 1)}
                        className="p-3 hover:bg-gray-50 transition-colors duration-200"
                        disabled={quantity <= 1}
                      >
                        <i className="ri-subtract-line text-sm" style={{ color: '#0B0B0B' }}></i>
                      </button>
                      <span 
                        className="px-6 py-3 text-lg font-medium min-w-[4rem] text-center"
                        style={{ 
                          color: '#0B0B0B',
                          fontFamily: 'Montserrat, sans-serif'
                        }}
                      >
                        {quantity}
                      </span>
                      <button
                        onClick={() => handleQuantityChange(quantity + 1)}
                        className="p-3 hover:bg-gray-50 transition-colors duration-200"
                        disabled={quantity >= 99}
                      >
                        <i className="ri-add-line text-sm" style={{ color: '#0B0B0B' }}></i>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Add to Cart Button */}
                <button
                  onClick={handleAddToCart}
                  className="w-full py-4 px-6 text-white font-bold text-lg rounded-lg transition-all duration-300 hover:bg-green-700 mb-4"
                  style={{ 
                    backgroundColor: '#0F3F2F',
                    fontFamily: 'Montserrat, sans-serif'
                  }}
                >
                  Adicionar ao Carrinho
                </button>

                {/* Product Features */}
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <i className="ri-truck-line text-lg" style={{ color: '#0F3F2F' }}></i>
                    <span 
                      className="text-sm"
                      style={{ 
                        color: '#C0C0C0',
                        fontFamily: 'Montserrat, sans-serif'
                      }}
                    >
                      Frete grátis para todo o Brasil
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <i className="ri-shield-check-line text-lg" style={{ color: '#0F3F2F' }}></i>
                    <span 
                      className="text-sm"
                      style={{ 
                        color: '#C0C0C0',
                        fontFamily: 'Montserrat, sans-serif'
                      }}
                    >
                      Garantia de qualidade premium
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <i className="ri-refresh-line text-lg" style={{ color: '#0F3F2F' }}></i>
                    <span 
                      className="text-sm"
                      style={{ 
                        color: '#C0C0C0',
                        fontFamily: 'Montserrat, sans-serif'
                      }}
                    >
                      Troca fácil em caso de defeito
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CartNotification
        show={showNotification}
        productName={`${product.brand} - ${product.name}`}
        onClose={() => setShowNotification(false)}
      />
    </>
  );
};

export default ProductModal;
